/** Audit spreadsheet tools — custom columns, spreadsheet preview, Excel download. */
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { X, Table2, Settings2, FileSpreadsheet, Plus, Trash2, ChevronUp, ChevronDown, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const cellVal = (r, key) => {
  if (key === "added_by_name") return r.added_by?.name || "";
  if (key.startsWith("custom:")) return "";
  const v = r[key];
  return v == null ? "" : String(v);
};

export const AuditTools = ({ year, records }) => {
  const [cfg, setCfg] = useState(null); // {columns, available_fields}
  const [showCols, setShowCols] = useState(false);
  const [showSheet, setShowSheet] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const loadCfg = async () => {
    try { setCfg((await api.get("/hr/audit-columns")).data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { loadCfg(); }, []);

  const downloadXlsx = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/hr/audit-records/export.xlsx?year=${year}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `Projexino_Audit_${year}.xlsx`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
      toast.success("Excel downloaded");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setDownloading(false); }
  };

  return (
    <>
      <button onClick={() => setShowCols(true)} data-testid="audit-columns-btn"
        className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-4 py-2 text-xs font-bold text-slate-700 hover:border-[#0F2042]">
        <Settings2 size={12} /> Columns
      </button>
      <button onClick={() => setShowSheet(true)} data-testid="audit-sheet-btn"
        className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-4 py-2 text-xs font-bold text-slate-700 hover:border-[#0F2042]">
        <Table2 size={12} /> Spreadsheet
      </button>
      <button onClick={downloadXlsx} disabled={downloading} data-testid="audit-xlsx-btn"
        className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 px-4 py-2 text-xs font-bold text-white hover:bg-emerald-700 disabled:opacity-60">
        {downloading ? <Loader2 size={12} className="animate-spin" /> : <FileSpreadsheet size={12} />} Excel
      </button>
      {showCols && cfg && (
        <ColumnsModal cfg={cfg} onClose={() => setShowCols(false)}
          onSaved={(c) => { setCfg(c); setShowCols(false); toast.success("Columns saved"); }} />
      )}
      {showSheet && cfg && (
        <SheetModal columns={cfg.columns} records={records} year={year}
          onClose={() => setShowSheet(false)} onDownload={downloadXlsx} />
      )}
    </>
  );
};

function ColumnsModal({ cfg, onClose, onSaved }) {
  const [cols, setCols] = useState(cfg.columns.map((c) => ({ ...c })));
  const [busy, setBusy] = useState(false);
  const fields = cfg.available_fields || [];
  const usedKeys = cols.map((c) => c.key);
  const move = (i, d) => {
    const j = i + d;
    if (j < 0 || j >= cols.length) return;
    const next = [...cols];
    [next[i], next[j]] = [next[j], next[i]];
    setCols(next);
  };
  const save = async () => {
    if (!cols.length) return toast.error("Keep at least one column");
    setBusy(true);
    try { onSaved((await api.put("/hr/audit-columns", { columns: cols })).data); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} onClick={onClose}
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4">
      <motion.div onClick={(e) => e.stopPropagation()} initial={{ scale: 0.97 }} animate={{ scale: 1 }}
        data-testid="audit-columns-modal" className="my-8 w-full max-w-xl rounded-2xl bg-white p-6 shadow-2xl">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-display text-lg font-bold text-[#0F2042]">Customize audit columns</h3>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <p className="mb-3 text-xs text-slate-500">Choose which columns appear (and their names) in the spreadsheet view and the Excel download. Reorder with the arrows.</p>
        <div className="space-y-2">
          {cols.map((c, i) => (
            <div key={i} className="flex items-center gap-2 rounded-xl border border-slate-200 p-2" data-testid={`audit-col-${i}`}>
              <div className="flex flex-col">
                <button onClick={() => move(i, -1)} data-testid={`audit-col-up-${i}`} className="text-slate-400 hover:text-[#0F2042]"><ChevronUp size={13} /></button>
                <button onClick={() => move(i, 1)} data-testid={`audit-col-down-${i}`} className="text-slate-400 hover:text-[#0F2042]"><ChevronDown size={13} /></button>
              </div>
              <select value={c.key} data-testid={`audit-col-key-${i}`}
                onChange={(e) => setCols(cols.map((x, xi) => (xi === i ? { ...x, key: e.target.value } : x)))}
                className="w-40 rounded-lg border border-slate-200 px-2 py-1.5 text-xs">
                {fields.map((f) => <option key={f} value={f}>{f.replace(/_/g, " ")}</option>)}
                {c.key.startsWith("custom:") && <option value={c.key}>custom (blank)</option>}
              </select>
              <input value={c.label} data-testid={`audit-col-label-${i}`}
                onChange={(e) => setCols(cols.map((x, xi) => (xi === i ? { ...x, label: e.target.value } : x)))}
                placeholder="Column name" className="flex-1 rounded-lg border border-slate-200 px-2 py-1.5 text-xs" />
              <button onClick={() => setCols(cols.filter((_, xi) => xi !== i))} data-testid={`audit-col-del-${i}`}
                className="rounded border border-rose-200 p-1.5 text-rose-400 hover:bg-rose-50"><Trash2 size={12} /></button>
            </div>
          ))}
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <button data-testid="audit-col-add-field" onClick={() => {
              const free = fields.find((f) => !usedKeys.includes(f)) || fields[0];
              setCols([...cols, { key: free, label: free.replace(/_/g, " ") }]);
            }}
            className="inline-flex items-center gap-1 rounded-full border border-slate-300 px-3 py-1.5 text-[11px] font-bold text-slate-600 hover:border-[#F97316]">
            <Plus size={11} /> Add field column
          </button>
          <button data-testid="audit-col-add-custom" onClick={() => setCols([...cols, { key: `custom:${Date.now()}`, label: "Custom column" }])}
            className="inline-flex items-center gap-1 rounded-full border border-slate-300 px-3 py-1.5 text-[11px] font-bold text-slate-600 hover:border-[#F97316]">
            <Plus size={11} /> Add blank column
          </button>
        </div>
        <button onClick={save} disabled={busy} data-testid="audit-cols-save"
          className="mt-4 w-full rounded-full bg-[#0F2042] py-2.5 text-sm font-bold text-white disabled:opacity-60">
          {busy ? <Loader2 size={14} className="mx-auto animate-spin" /> : "Save columns"}
        </button>
      </motion.div>
    </motion.div>
  );
}

function SheetModal({ columns, records, year, onClose, onDownload }) {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} onClick={onClose}
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4">
      <motion.div onClick={(e) => e.stopPropagation()} initial={{ scale: 0.97 }} animate={{ scale: 1 }}
        data-testid="audit-sheet-modal" className="my-8 w-full max-w-4xl rounded-2xl bg-white p-6 shadow-2xl">
        <div className="mb-3 flex items-center justify-between">
          <div>
            <h3 className="font-display text-lg font-bold text-[#0F2042]">Audit spreadsheet — {year}</h3>
            <p className="text-xs text-slate-500">{records.length} record{records.length !== 1 ? "s" : ""} · exactly what the Excel download contains</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onDownload} data-testid="audit-sheet-download"
              className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 px-4 py-2 text-xs font-bold text-white hover:bg-emerald-700">
              <FileSpreadsheet size={12} /> Download Excel
            </button>
            <button onClick={onClose} data-testid="audit-sheet-close"><X size={18} /></button>
          </div>
        </div>
        <div className="max-h-[60vh] overflow-auto rounded-xl border border-slate-200">
          <table className="w-full text-xs" data-testid="audit-sheet-table">
            <thead className="sticky top-0">
              <tr>
                {columns.map((c, i) => (
                  <th key={i} className="whitespace-nowrap bg-[#0F2042] px-3 py-2 text-left font-bold text-white">{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {records.map((r, ri) => (
                <tr key={r.id || ri} className={ri % 2 ? "bg-slate-50" : "bg-white"}>
                  {columns.map((c, ci) => (
                    <td key={ci} className="whitespace-nowrap border-t border-slate-100 px-3 py-1.5">{cellVal(r, c.key)}</td>
                  ))}
                </tr>
              ))}
              {records.length === 0 && (
                <tr><td colSpan={columns.length} className="p-6 text-center text-slate-400">No audit records for {year}.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </motion.div>
    </motion.div>
  );
}

